// src/utils/taxCalculator.js

/**
 * Calculates income tax based on gross amount and tax bracket
 * This is a simplified calculation for demonstration purposes
 * A real implementation would use proper tax tables and rules
 * 
 * @param {number} grossAmount - Gross income amount
 * @param {string} taxBracket - Tax bracket identifier
 * @param {Date} paymentDate - Date of payment for tax year determination
 * @returns {number} Calculated tax amount
 */
export const calculateTax = (grossAmount, taxBracket = 'standard', paymentDate = new Date()) => {
    // Get tax year based on payment date
    const taxYear = paymentDate.getFullYear();
    
    // These would be fetched from a database in a real application
    const taxRates = {
      '2025': {
        standard: [
          { threshold: 0, rate: 0.1 },
          { threshold: 10000, rate: 0.15 },
          { threshold: 40000, rate: 0.25 },
          { threshold: 80000, rate: 0.30 },
          { threshold: 160000, rate: 0.35 }
        ],
        reduced: [
          { threshold: 0, rate: 0.05 },
          { threshold: 10000, rate: 0.10 },
          { threshold: 40000, rate: 0.20 },
          { threshold: 80000, rate: 0.25 },
          { threshold: 160000, rate: 0.30 }
        ],
        exempt: [
          { threshold: 0, rate: 0 }
        ]
      },
      '2024': {
        standard: [
          { threshold: 0, rate: 0.1 },
          { threshold: 11000, rate: 0.15 },
          { threshold: 44000, rate: 0.25 },
          { threshold: 89000, rate: 0.30 },
          { threshold: 170000, rate: 0.35 }
        ],
        reduced: [
          { threshold: 0, rate: 0.05 },
          { threshold: 11000, rate: 0.10 },
          { threshold: 44000, rate: 0.20 },
          { threshold: 89000, rate: 0.25 },
          { threshold: 170000, rate: 0.30 }
        ],
        exempt: [
          { threshold: 0, rate: 0 }
        ]
      }
    };
    
    // Default to most recent year if tax year not found
    const yearRates = taxRates[taxYear.toString()] || taxRates[Object.keys(taxRates).sort().pop()];
    
    // Get rates for the specified bracket or default to standard
    const bracketRates = yearRates[taxBracket] || yearRates.standard;
    
    // Calculate tax progressively through brackets
    let taxAmount = 0;
    let remainingIncome = grossAmount;
    
    for (let i = 0; i < bracketRates.length; i++) {
      const currentBracket = bracketRates[i];
      const nextBracket = bracketRates[i + 1];
      
      if (!nextBracket) {
        // We're at the highest bracket
        taxAmount += remainingIncome * currentBracket.rate;
        break;
      } else {
        // Calculate tax for this bracket
        const amountInBracket = Math.min(remainingIncome, nextBracket.threshold - currentBracket.threshold);
        taxAmount += amountInBracket * currentBracket.rate;
        remainingIncome -= amountInBracket;
        
        if (remainingIncome <= 0) break;
      }
    }
    
    return Math.round(taxAmount * 100) / 100; // Round to 2 decimal places
  };
  
  /**
   * Calculates employer contributions based on salary
   * 
   * @param {number} grossAmount - Gross salary amount
   * @param {number} rate - Employer contribution rate (as decimal)
   * @returns {number} Employer contribution amount
   */
  export const calculateEmployerContribution = (grossAmount, rate = 0.1) => {
    return Math.round((grossAmount * rate) * 100) / 100;
  };